import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://tag-generator-api.tchallasolutions.com/api';

// Debug mode - only show logs in development
const DEBUG_MODE = process.env.REACT_APP_DEBUG === 'true' || process.env.NODE_ENV === 'development';

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and logging
axiosInstance.interceptors.request.use(
  (config) => {
    // Check localStorage first, then sessionStorage
    let token = localStorage.getItem('token');
    if (!token) {
      token = sessionStorage.getItem('token');
    }
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Enhanced API logging (only in debug mode)
    if (DEBUG_MODE) {
      const timestamp = new Date().toISOString();
      const logData = {
        timestamp,
        method: config.method?.toUpperCase(),
        url: `${config.baseURL}${config.url}`,
        endpoint: config.url,
        hasAuth: !!token,
        params: config.params,
        data: config.data
      };

      console.group(`🚀 API REQUEST [${logData.method}] ${logData.endpoint}`);
      console.log('📍 Full URL:', logData.url);
      console.log('🔐 Has Auth Token:', logData.hasAuth);
      if (logData.params) console.log('📋 Query Params:', logData.params);
      if (logData.data) console.log('📦 Request Data:', logData.data);
      console.log('⏰ Timestamp:', timestamp);
      console.groupEnd();
    }

    // Store request info for response logging
    config.metadata = { startTime: Date.now(), logData: DEBUG_MODE ? {
      timestamp: new Date().toISOString(),
      method: config.method?.toUpperCase(),
      url: `${config.baseURL}${config.url}`,
      endpoint: config.url,
      hasAuth: !!token,
      params: config.params,
      data: config.data
    } : null };

    return config;
  },
  (error) => {
    if (DEBUG_MODE) {
      console.error('❌ REQUEST ERROR:', error);
    }
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and logging
axiosInstance.interceptors.response.use(
  (response) => {
    // Enhanced response logging (only in debug mode)
    if (DEBUG_MODE) {
      const config = response.config;
      const duration = config.metadata ? Date.now() - config.metadata.startTime : 0;
      const logData = config.metadata?.logData;

      console.group(`✅ API RESPONSE [${response.status}] ${logData?.endpoint || config.url}`);
      console.log('📍 Full URL:', logData?.url || `${config.baseURL}${config.url}`);
      console.log('📊 Status:', `${response.status} ${response.statusText}`);
      console.log('⏱️ Duration:', `${duration}ms`);
      console.log('📦 Response Data:', response.data);
      if (response.headers) console.log('📋 Response Headers:', response.headers);
      console.groupEnd();
    }

    return response;
  },
  (error) => {
    // Enhanced error logging (only in debug mode)
    if (DEBUG_MODE) {
      const config = error.config;
      const duration = config?.metadata ? Date.now() - config.metadata.startTime : 0;
      const logData = config?.metadata?.logData;

      console.group(`❌ API ERROR [${error.response?.status || 'NETWORK'}] ${logData?.endpoint || config?.url}`);
      console.log('📍 Full URL:', logData?.url || (config ? `${config.baseURL}${config.url}` : 'Unknown'));
      console.log('📊 Status:', error.response?.status || 'Network Error');
      console.log('⏱️ Duration:', `${duration}ms`);
      console.log('💥 Error Message:', error.message);
      if (error.response?.data) console.log('📦 Error Data:', error.response.data);
      if (error.response?.headers) console.log('📋 Error Headers:', error.response.headers);
      console.log('🔍 Full Error Object:', error);
      console.groupEnd();
    }

    if (error.response?.status === 401) {
      // Clear all auth data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('rememberMe');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
